import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { 
  AlertTriangle, 
  Shield, 
  CheckCircle, 
  ArrowRight,
  Globe,
  BookOpen,
  FileCheck,
  Search,
  Scale,
  Building2
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=2000&q=80";

const commonRisks = [
  {
    title: "Fraudulent Land Titles",
    description: "Properties sold with fake or forged title documents that do not confer legal ownership."
  },
  {
    title: "Multiple Sales of the Same Property",
    description: "The same land sold to multiple buyers, leaving only one—if any—with valid ownership."
  },
  {
    title: "Unregistered or Defective Documentation",
    description: "Transactions completed without proper registration, leaving ownership legally unprotected."
  },
  {
    title: "Hidden Encumbrances or Disputes",
    description: "Properties affected by existing mortgages, court cases, or family disputes not disclosed to buyers."
  }
];

const protectionServices = [
  {
    icon: Search,
    title: "Title Verification Through Land Registries",
    description: "Independent searches to confirm document authenticity and legal ownership status."
  },
  {
    icon: FileCheck,
    title: "Ownership History Investigation",
    description: "Tracing the chain of ownership to identify any gaps, disputes, or irregularities."
  },
  {
    icon: Scale,
    title: "Contract Review and Documentation Structuring",
    description: "Ensuring agreements are legally sound and protect your interests."
  },
  {
    icon: Building2,
    title: "Transaction Oversight Through Closing",
    description: "Guiding the acquisition from offer to completion with legal supervision at every stage."
  }
];

export default function SafePropertyGuidePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <main className="min-h-screen bg-[#FAFAF9]" data-testid="safe-property-guide-page">
      <Navigation scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-[#0A0A0B]/85" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 lg:px-12 py-32 md:py-40 text-center">
          <span className="inline-flex items-center gap-2 text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] uppercase font-semibold mb-6">
            <BookOpen className="w-4 h-4" />
            Legal Guide
          </span>
          
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white leading-tight mb-6">
            Guide to Buying Property
            <span className="block text-[#D4AF37] italic mt-2">in Nigeria Safely</span>
          </h1>
          
          <div className="w-16 md:w-20 h-px bg-[#D4AF37] mx-auto" />
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center space-y-6">
            <p className="text-[#1A1A1A]/70 text-base md:text-lg leading-[1.9]">
              Buying property in Nigeria can be a highly rewarding investment, but it also carries 
              significant risks if legal due diligence is not properly conducted. Many investors—especially 
              those living abroad—lose money due to fraudulent titles, unclear ownership records, or 
              poorly structured transactions.
            </p>
            <p className="text-[#1A1A1A]/70 text-base md:text-lg leading-[1.9]">
              4 ACES Attorneys provides strategic legal guidance designed to protect investors and 
              ensure that property acquisitions in Nigeria are legally secure.
            </p>
          </div>
        </div>
      </section>

      {/* Common Risks Section */}
      <section className="py-16 md:py-24 bg-[#0A0A0B]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] uppercase font-semibold">
              Understanding the Challenges
            </span>
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-white mt-4 mb-6">
              Common Risks When Buying
              <span className="block text-[#D4AF37] italic">Property in Nigeria</span>
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-10">
            {commonRisks.map((risk, index) => (
              <div 
                key={index}
                className="p-6 md:p-8 border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 md:w-6 md:h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-['Playfair_Display'] text-lg md:text-xl text-white mb-2">
                      {risk.title}
                    </h3>
                    <p className="text-white/60 text-sm md:text-base leading-relaxed">
                      {risk.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center p-6 md:p-8 border border-[#D4AF37]/30 bg-[#D4AF37]/5">
            <p className="text-white/80 text-sm md:text-base">
              <strong className="text-[#D4AF37]">Important:</strong> These risks can be avoided through 
              proper legal investigation conducted before any payment is made. A qualified property 
              lawyer can identify these issues and protect your investment.
            </p>
          </div>
        </div>
      </section>

      {/* How We Protect Investors */}
      <section className="py-16 md:py-24 bg-[#FAFAF9]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] uppercase font-semibold">
              Our Approach
            </span>
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-[#1A1A1A] mt-4 mb-4">
              How 4 ACES Attorneys
              <span className="block text-[#D4AF37] italic">Protects Property Investors</span>
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto mb-6" />
            <p className="text-[#1A1A1A]/60 text-base md:text-lg max-w-2xl mx-auto">
              Our firm conducts comprehensive legal due diligence before any property purchase is completed.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {protectionServices.map((service, index) => (
              <div 
                key={index}
                className="p-6 md:p-8 bg-white border border-[#E5E5E5] hover:border-[#D4AF37]/50 transition-colors group"
              >
                <div className="flex items-start gap-4 md:gap-5">
                  <div className="w-12 h-12 md:w-14 md:h-14 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 transition-all">
                    <service.icon className="w-5 h-5 md:w-6 md:h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-['Playfair_Display'] text-lg md:text-xl text-[#1A1A1A] mb-2 md:mb-3">
                      {service.title}
                    </h3>
                    <p className="text-[#1A1A1A]/60 text-sm md:text-base leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diaspora Investors Section */}
      <section className="py-16 md:py-24 bg-[#0A0A0B]">
        <div className="max-w-4xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] uppercase font-semibold">
              For Nigerians Abroad
            </span>
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-white mt-4 mb-6">
              Legal Advisory for
              <span className="block text-[#D4AF37] italic">Diaspora Investors</span>
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>

          <div className="text-center space-y-6 mb-10">
            <p className="text-white/70 text-base md:text-lg leading-[1.9]">
              Nigerians living abroad frequently rely on family members or informal agents when 
              purchasing property. Without proper legal oversight, this exposes investors to 
              significant financial risk.
            </p>
            <p className="text-white/70 text-base md:text-lg leading-[1.9]">
              4 ACES Attorneys regularly advises diaspora clients in the United Kingdom, United States, 
              Canada, and the Middle East seeking secure real estate investments in Nigeria.
            </p>
          </div>

          {/* Location badges */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {["United Kingdom", "United States", "Canada", "Middle East", "Nigeria"].map((location) => (
              <div 
                key={location}
                className="flex items-center gap-2 border border-[#D4AF37]/25 px-4 py-2 md:px-5 md:py-2.5"
              >
                <Globe className="w-3 h-3 md:w-4 md:h-4 text-[#D4AF37]" />
                <span className="text-white/60 text-xs md:text-sm">{location}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-[#FAFAF9]">
        <div className="max-w-3xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] uppercase font-semibold">
            Take Action
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-[#1A1A1A] mt-4 mb-6">
            Speak with a Property Lawyer
            <span className="block text-[#D4AF37] italic">Before You Buy</span>
          </h2>
          <div className="w-16 h-px bg-[#D4AF37] mx-auto mb-8" />
          
          <p className="text-[#1A1A1A]/60 text-base md:text-lg leading-[1.9] mb-10">
            If you are considering purchasing property in Nigeria, a brief legal consultation 
            can help you avoid costly mistakes.
          </p>

          <a
            href="/#booking"
            className="inline-flex items-center gap-2 md:gap-3 bg-[#D4AF37] text-[#0A0A0B] px-8 md:px-10 py-4 md:py-5 font-semibold text-sm tracking-wide hover:bg-[#C5A028] transition-colors"
            data-testid="guide-cta-button"
          >
            Book a Private Consultation
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
